import type { Decision } from "../dashboard/deskLogic";
import { viewerNote as deskViewerNote } from "../dashboard/deskLogic";
import { byText, type RegisterConfig } from "../register/registerLogic";

/**
 * The school counsellor's dashboard: work waiting, the week's appointments
 * and the caseload. Mirrors apps/dashboard/counselling.py -- scoped like the
 * Counselling page and carrying no notes, only who, when and what kind.
 */

interface Learner { student: number; learner: string; admission_number: string; class_name: string }
export interface Appointment extends Learner {
  id: number; date: string; time: string; minutes: number; session_type: string; status: string; counsellor: string
}
export interface CaseFile extends Learner {
  open_cases: number; severity: "" | "low" | "medium" | "high" | "critical"; has_plan: boolean
  plan_review: string | null; last_session: string | null; next_session: string | null
}
export interface CounsellingDesk {
  generated_at: string; today: string; viewer_role: string
  work: Decision[]
  week: Appointment[]
  caseload: CaseFile[]
  figures: { seen_this_month: number; no_shows_this_month: number; open_cases: number; active_plans: number }
}

/** The Counselling page's sessions, searched to one learner (as its own Learners tab does). */
export const learnerLink = (row: Pick<Learner, "admission_number" | "learner">) => `/counseling?learner=${encodeURIComponent(row.admission_number || row.learner)}`;

type Tone = { label: string; dot: string };
export function severityTone(severity: CaseFile["severity"]): Tone {
  if (severity === "critical") return { label: "Critical", dot: "bg-red-500" };
  if (severity === "high") return { label: "High", dot: "bg-amber-500" };
  if (severity === "medium") return { label: "Medium", dot: "bg-sky-500" };
  if (severity === "low") return { label: "Low", dot: "bg-gray-400" };
  return { label: "No open case", dot: "bg-gray-300" };
}

/** What the file needs next, in plain words. */
export function nextStep(file: CaseFile, today: string) {
  if (file.plan_review && file.plan_review <= today) return "Review the support plan";
  if (!file.next_session && file.open_cases) return "Book a session";
  if (file.next_session) return `Next session ${file.next_session === today ? "today" : file.next_session}`;
  return "Keep in view";
}

export const caseloadConfig = (today: string): RegisterConfig<CaseFile> => ({
  search: (row) => `${row.learner} ${row.admission_number} ${row.class_name}`,
  views: [
    { key: "all", label: "All" },
    { key: "serious", label: "High or critical", match: (row) => row.severity === "high" || row.severity === "critical" },
    { key: "unbooked", label: "Nothing booked", match: (row) => row.open_cases > 0 && !row.next_session },
    { key: "review", label: "Plan review due", match: (row) => Boolean(row.plan_review && row.plan_review <= today) },
  ],
  category: { label: "Class", value: (row) => row.class_name || "No class" },
  sorts: [
    { key: "severity", label: "Most serious first", compare: () => 0 },
    { key: "learner", label: "Learner A–Z", compare: byText((row) => row.learner) },
    { key: "last", label: "Longest since seen", compare: byText((row) => row.last_session ?? "") },
  ],
});

/** Appointments grouped by day, in date order. */
export function byDay(rows: Appointment[]) {
  const days = new Map<string, Appointment[]>();
  for (const row of rows) days.set(row.date, [...(days.get(row.date) ?? []), row]);
  return [...days.entries()].map(([date, appointments]) => ({ date, appointments }));
}

export const viewerNote = (role: string) =>
  role === "school_counsellor" ? "" : deskViewerNote(role, "school_counsellor", "the counsellor's dashboard").replace(" The decisions listed are the ones your own account may take, and appraisals are those assigned to you.", role === "admin" ? " As an administrator you see the whole counselling service; no notes are shown here." : " You see only your own counselling work.");
